import { Dayjs } from "dayjs";
import { ConsumptionTypes } from "shared/constants";
import { Unit } from "shared/helpers";
import {
  ConfigDynamicTrackerFields,
  MaterialCategoriesItem,
  MaterialStockItem,
  SupplierItemWithCost,
} from "../materials/models";
import { ProcessTemplateItem } from "../productCard/models";

export const enum OrderStatuses {
  AC = "AC",
  AP = "AP",
  CO = "CO",
  DE = "DE",
  PE = "PE",
  PO = "PO",
}

export interface ProductCard {
  id: number;
  name: string;
  company: number[];
  process_steps: number[];
  is_archived?: boolean;
}

export interface ExpandedBuyerItem {
  id: number;
  name: string;
  address: string;
  payment_term_net_days: number;
  inco_terms: string;
  is_supplier: boolean;
  is_buyer: boolean;
}

export type DynamicKeyValue = string | number;

export interface DynamicOrderObject {
  [key: string]: {
    [field: string]: DynamicKeyValue;
  };
}

export interface ProcessStepTrackerItem {
  id: number;
  order: number;
  process_step: {
    id: number;
    name: string;
    process_template: ProcessTemplateItem;
    dynamic_process_step_fields: DynamicOrderObject;
    input_materials: number[];
    output_materials: number[];
  };
  machine: null;
  start_datetime: null;
  end_datetime: null;
  status: string;
}

export interface CalcProducedPerMaterial {
  material_id: number;
  material_name: string;
  item_target: number;
  item_current: number;
  item_percentage: number;
  quantity_target: number;
  quantity_current: number;
  quantity_percentage: number;
}

export interface CalcProducedPerProcess {
  order_process_step_tracker_id: number;
  process_name: string;
  item_target: number;
  item_current: number;
  item_percentage: number;
  quantity_target: number;
  quantity_current: number;
  quantity_percentage: number;
  per_material: {
    [materialId: string]: CalcProducedPerMaterial;
  };
}

export interface CalcProduced {
  order_id: number;
  item_target: number;
  item_current: number;
  item_percentage: number;
  quantity_target: number;
  quantity_current: number;
  quantity_percentage: number;
  per_process: {
    [trackerId: string]: CalcProducedPerProcess;
  };
}

export interface OrderItem {
  id: number;
  buyer: ExpandedBuyerItem;
  po_number: string;
  wanted_delivery_datetime: string;
  production_datetime: string;
  product_card: ProductCard;
  quantity_expected: number;
  status: OrderStatuses;
  dynamic_order_fields: DynamicOrderObject;
  calc_count_process_step_trackers_pending: number;
  calc_count_process_step_trackers_in_progress: number;
  calc_count_process_step_trackers_finished: number;
  process_step_trackers: ProcessStepTrackerItem[];
  order_code?: string;
  calc_progress?: string;
  customer_order_number: string;
  calc_produced?: CalcProduced;
}

export interface OrdersResponse {
  count: number;
  next: string;
  previous: string;
  results: OrderItem[];
}

export interface OrdersParams {
  offset?: number | null;
  limit?: number | null;
  expand?: string;
  ordering?: string | null;
  po_number__icontains?: string | null;
  quantity_expected__gte?: string | number;
  quantity_expected__lte?: string | number;
  wanted_delivery_datetime__gte?: string | number;
  wanted_delivery_datetime__lte?: string | number;
  production_datetime__gte?: string | number;
  production_datetime__lte?: string | number;
  status?: string;
  buyer?: number | string;
  order_code?: number | string;
  order_code__icontains?: string | null;
  status__in?: string[];
  include_calc_progress?: boolean;
  process_step_trackers__machine__in?: number[];
  order?: number;
  product_card__name__icontains?: string;
  product_card?: number;
}

export interface GetOrderByIdParams {
  id?: number;
  expand?: string;
  order_code?: string;
}

export interface CreateOrderItem {
  buyer: number;
  po_number: string;
  wanted_delivery_datetime: string;
  production_datetime: string;
  product_card: number;
  quantity_expected: number;
  status: string;
  dynamic_order_fields: DynamicOrderObject;
  customer_order_number: string;
}

export interface UpdateOrderItem {
  id: number;
  buyer: number;
  po_number: string;
  wanted_delivery_datetime: string;
  production_datetime: string;
  product_card: number;
  quantity_expected: number;
  status: string;
  dynamic_order_fields: DynamicOrderObject;
  customer_order_number: string;
}

export interface OrderStepTrackerParams {
  id?: number;
  expand?: string;
  order__status?: string;
  process_step__process_template?: number | string;
}

export interface ConfigStepFields {
  name: string;
  type: string;
  description: string;
  choices: number | number[] | string[];
  required: boolean;
  value?: any;
}

export interface InputMaterials {
  id: number;
  materialName?: string;
  selectValue: string;
  fields: InputMaterialFields[];
}

export interface DynamicOutputMaterialObject {
  [key: string]: {
    [field: string]: DynamicKeyValue;
  };
}
export interface OutputMaterial {
  id: number;
  name: string;
  materialName?: string;
  material_category: OutputMaterialMaterialCategory;
  suppliers: number[];
  target_quantity_in_stock: number;
  numberOfMaterialItems?: number;
  dynamicData?: {
    quantity: number;
    dynamic_tracker_fields?: DynamicOutputMaterialObject;
    status: string;
  }[];
}

export interface OutputMaterialMaterialCategory {
  id: number;
  name: string;
  unit: number;
  config_dynamic_tracker_fields: ConfigDynamicTrackerFields[];
}

export interface InputMaterialCategory {
  id: number;
  name: string;
  unit: number;
  default_material_consume_variant: string;
  lock_material_consume_variant: false;
  config_dynamic_tracker_fields: ConfigDynamicTrackerFields;
  unique_code: string;
  suppliers: number[];
}

export interface DynamicOutputMaterialData {
  quantity: number;
  dynamic_tracker_fields?: DynamicOutputMaterialObject;
  status: string;
}
export interface ProductionOutputMaterial {
  id: number;
  unit?: Unit;
  numberOfMaterialItems?: number;
  default_number_of_items: number;
  dynamicData: DynamicOutputMaterialData[];
  material: {
    id: number;
    name: string;
    material_category: OutputMaterialMaterialCategory;
    suppliers: SupplierItemWithCost[];
    target_quantity_in_stock: number;
    n_packaging_items: number;
  };
}

export interface ConsumptionProportionalMaterial {
  id: number;
  name: string;
  material_category: number;
  target_quantity_in_stock: number;
  n_packaging_items: number;
}

export interface InputMaterialFields {
  variant: string;
  quantity: number;
  selectValue?: string;
}

export interface ProductionInputMaterial {
  consumptionType: ConsumptionTypes;
  consumptionFixedValue?: number | null;
  consumptionProportionalValue?: number | null;
  consumptionProportionalMaterial?: ConsumptionProportionalMaterial;
  id: number;
  material: {
    id: number;
    name: string;
    material_category: InputMaterialCategory;
    suppliers: SupplierItemWithCost[];
    target_quantity_in_stock: number;
    n_packaging_items: number;
  };
  materialName?: string;
  selectValue: string;
  fields: InputMaterialFields[];
  consumption_type: ConsumptionTypes;
  consumption_fixed_value?: number | null;
  consumption_proportional_value?: number | null;
  consumption_proportional_material?: ConsumptionProportionalMaterial;
  materialCategory?: MaterialCategoriesItem;
  calc_hide_field?: boolean;
  calcHideField?: boolean;
  info?: string;
}

export interface OrderStepTrackerResponseItem {
  id: number;
  order: {
    id: number;
    buyer: {
      id: number;
      name: string;
      address: string;
      payment_term_net_days: number;
      inco_terms: string;
      is_supplier: false;
      is_buyer: true;
      config_dynamic_order_fields: null;
      product_cards: [];
    };
    order_code?: string;
    po_number: string;
    wanted_delivery_datetime: string;
    production_datetime: string;
    product_card: number;
    quantity_expected: number;
    status: string;
    dynamic_order_fields: null;
    process_step_trackers: number[];
  };
  process_step: {
    id: number;
    name: string;
    process_template: {
      id: number;
      name: string;
      description: string;
      config_dynamic_process_step_fields: [
        {
          title: string;
          fields: ConfigStepFields[];
        }
      ];
    };
    dynamic_process_step_fields: DynamicOrderObject;
    input_materials: ProductionInputMaterial[];
    output_materials: ProductionOutputMaterial[];
  };
  machine: number;
  start_datetime: string;
  end_datetime: string;
  status: string;
  variant: string | null;
  variantValue: number | null;
  input_materials: InputMaterials[];
  calc_default_dynamic_tracker_fields: DynamicOutputMaterialObject[] | null;
  info?: string | null;
}

export interface OrderProcessStepItem {
  id: number;
  order: {
    id: number;
    buyer: number;
    po_number: string;
    wanted_delivery_datetime: string;
    production_datetime: string;
    product_card: number;
    quantity_expected: number;
    status: string;
    dynamic_order_fields: null;
    process_step_trackers: number[];
  };
  process_step: {
    id: number;
    name: string;
    process_template: number;
    dynamic_process_step_fields: DynamicOrderObject;
    input_materials: number[];
    output_materials: number[];
  };
  machine: null | number;
  start_datetime: null | string;
  end_datetime: null | string;
  status: string;
}

export interface InfoBoxField {
  name: string;
  value: string | null;
}

export interface OrderProcessInfoBox {
  title: string;
  fields: InfoBoxField[];
}

export interface UpdateOrderStepTrackerItem {
  id: number;
  machine: number;
  start_datetime: string;
  end_datetime: string;
  status?: string;
  info?: string | null;
}

export interface UpdateOrderStepTrackerItemExpanded {
  id?: number;
  input_materials: InputMaterialFormik[];
  output_materials: OutputMaterialsFormik[];
}

export interface InputMaterialFormik {
  id: string | number;
  variant: string | null;
  quantity: number;
}

export interface OutputMaterialsFormik {
  material_id: number;
  items_to_produce: [
    {
      quantity: number;
      dynamic_tracker_fields: any;
      status: string;
    }
  ];
}

export interface OrdersPlansParams {
  offset?: number | null;
  limit?: number | null;
  expand?: string;
  ordering?: string | null;
}

export interface OrdersPlansResponse {
  count: number;
  next: string;
  previous: string;
  results: OrderPlan[];
}

export interface OrderPlan {
  id?: number;
  name: string;
  status: string;
  // order_plan_for_machine?: OrderMachinePlan[];
  order_plan_for_machine?: OrderPlanForMachine[];
}

export interface OrderPlanForMachine {
  id: number;
  order_plan: number;
  machine: number;
  order_process_step_tracker: {
    id: number;
    order: {
      id: number;
      buyer: number;
      po_number: string;
      wanted_delivery_datetime: string;
      production_datetime: string;
      product_card: number;
      quantity_expected: number;
      status: string;
      dynamic_order_fields: null;
      process_step_trackers: number[];
      calc_count_process_step_trackers_pending: number;
      calc_count_process_step_trackers_in_progress: number;
      calc_count_process_step_trackers_finished: number;
      calc_count_process_step_trackers: number;
    };
    process_step: {
      id: number;
      name: string;
      process_template: number;
      dynamic_process_step_fields: any[];
      input_materials: number[];
      output_materials: number[];
    };
    machine: number;
    start_datetime: string;
    end_datetime: string;
    status: string;
    calc_position_in_order: number;
  };
  start_datetime: string;
  end_datetime: string;
}

export interface OrderMachinePlan {
  id: number;
  order_plan: number;
  machine: number;
  order_process_step_tracker?: number;
  start_datetime: number | string | Dayjs;
  end_datetime: number | string | Dayjs;
}

export interface OrderPlansForMachineResponse {
  count: number;
  next: string;
  previous: string;
  results: OrderMachinePlan[];
}

export interface OrderPlansForMachineParams {
  id?: number;
  offset?: number | null;
  limit?: number | null;
  expand?: string;
  ordering?: string | null;
}

export interface CreateOrderPlanForMachine {
  order_plan?: number;
  machine?: number;
  order_process_step_tracker: number;
  start_datetime: string | Dayjs;
  end_datetime: string | Dayjs;
}

export interface MoveItemsParams {
  id: number;
  material_item_ids: number[];
}

export interface GenerateMultiplePackagingReport {
  id: number;
  material_id: number | null;
  packaging_ids: number[] | null;
}

export interface GenerateOrderProcessStepLabel {
  id: number;
}

export interface CorrectAllPackagingParams {
  id: number;
}
export interface CorrectAllPackagingResponse {
  message: string;
}

export interface MaterialItemsMassUpdate {
  material_items: number[];
  status?: string | null;
  situation?: string | null;
  moved_to_order?: number | null;
}

export interface Quantity {
  material_id: number;
  material_name: string;
  count_items: number;
  quantity_total: number | string;
}

export interface Target {
  tracker_id: number;
  tracker_name: string;
  tracker_info: string;
  quantities: Quantity[];
}

export interface TargetsModalProps {
  isOpen: boolean;
  orderId: number | null;
  onClose: () => void;
  selectedOrderCode: string | null;
}

export interface StartWorkingPayload {
  machine?: number | null;
  start_datetime?: string | null;
  end_datetime?: string | null;
  status?: string | null;
  info?: string | null;
}

export interface ScannedMaterialCategory {
  id: number;
  name: string;
  unit: number;
  default_material_consume_variant: string;
  lock_material_consume_variant: boolean;
  config_dynamic_tracker_fields: any[];
  unique_code: string;
  export_col_for_serial_number: string | null;
  export_col_for_quantity: string | null;
}

export interface ScannedMaterial {
  id: number;
  name: string;
  expanded_label_name: string;
  material_category: ScannedMaterialCategory;
  suppliers: any[];
  target_quantity_in_stock: number;
  n_packaging_items: number;
  precalc_total_original_quantity: number;
  precalc_total_remaining_quantity: number;
}

export interface ScannedMaterialStockBatch {
  id: number;
  material: ScannedMaterial;
  batch_code: string;
  is_quantity_in_tracker_table: boolean;
  quantity: number | null;
  delivery_date: string;
  status: string;
  supplier: any;
  produced_in_order_process_step_tracker: number;
}

export interface ScannedMaterialItem {
  id: number;
  material_stock_batch: ScannedMaterialStockBatch;
  packaging: number;
  serial_number: string;
  quantity: number;
  dynamic_tracker_fields: Record<string, any>;
  status: string;
  timestamp: string;
  order_production_set: number;
  order_production_set_position: number;
  production_number: number;
  calc_is_moved: boolean;
  situation: string;
  moved_to_order: any;
  last_timestamp_scanned: string;
}

export interface ScannedUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: any[];
  access_points: string[];
  menu_keys: string[];
  is_superuser: boolean;
}

export interface ScannedItem {
  id: number;
  timestamp: string;
  serial_number: string;
  is_error: boolean;
  result: "FOUND" | "NOT_FOUND";
  user: ScannedUser;
  material_item: MaterialStockItem;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ScanItemRequest {
  serial_number: string;
}

export interface ScanItemResponse extends ScannedItem {}
